// Shared time formatting.
//
// These were previously reimplemented in six places (AuditLog, ChangeRequests,
// Sidebar, FlagTable, Promotions, Environments) with small inconsistencies —
// "just now" vs "Just now", with and without a clamp on future timestamps, and
// only AuditLog falling back to a date past 30 days.

/**
 * A short, human-readable age: "just now", "5m ago", "3h ago", "2d ago".
 *
 * Past 30 days it falls back to a locale date, since "412d ago" is harder to
 * read than the date itself. Null/undefined render as `emptyLabel` ("Never" by
 * default) so callers don't each guard.
 *
 * Future timestamps clamp to "just now" rather than rendering a negative age —
 * clock skew between the browser and the API makes that reachable in practice.
 */
export function relativeTime(iso: string | null | undefined, emptyLabel = 'Never'): string {
  if (!iso) return emptyLabel

  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return emptyLabel

  const seconds = Math.floor(Math.max(0, Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`

  return new Date(iso).toLocaleDateString()
}

/**
 * The full timestamp, for `title` attributes alongside a relative time.
 *
 * "Today at 02:15 PM" / "Mar 4 at 02:15 PM" / "Mar 4, 2025 at 02:15 PM" — the
 * year only appears when it isn't the current one, which keeps the common case
 * short.
 */
export function absoluteTime(iso: string | null | undefined, emptyLabel = 'Never'): string {
  if (!iso) return emptyLabel

  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return emptyLabel

  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const now = new Date()

  if (d.toDateString() === now.toDateString()) return `Today at ${time}`

  const dateOpts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }

  return `${d.toLocaleDateString([], dateOpts)} at ${time}`
}
